import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Field, Formik } from 'formik';
import _ from 'lodash';
import { AddressType, CountryType, RelationshipType, SexType, StateType, SupportPayer, SupportSubscriber, CreateClaimSubscriber, ProviderPayer } from '../../../../api';
import { getOptions, ISelectOption } from '../../../base/select';
import { ActionMeta, MultiValue } from 'react-select';
import * as S from '../form.styles';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import { BaseInput } from '../../../base/input';
import { BaseSelect } from '../../../base/select';
import { AsyncSelectField } from '../../../base/form/async-select/async-select';
import { DatePickerField } from '../../../base/form/datepicker/date-picker';
import * as Yup from 'yup';
import { FormErrorBanner } from '../../../base/form/form-error-banner';

interface SubscriberPayerFormValues {
    subscriberLookup?: string;
    subscriberId?: string;
    payerLookup?: string;
    payerId?: string;
    memberId?: string;
    first: string;
    middle?: string;
    last: string;
    dob?: string;
    sex?: SexType | '';
    phone?: string;
    relationshipToPatient?: RelationshipType | '';
    address1: string;
    address2?: string;
    city: string;
    state?: StateType | '';
    country?: CountryType | '';
    postal: string;
}

const emptyValues: SubscriberPayerFormValues = {
    subscriberLookup: '',
    subscriberId: '',
    payerLookup: '',
    payerId: '',
    memberId: '',
    first: '',
    middle: '',
    last: '',
    dob: '',
    sex: '' as any,
    phone: '',
    relationshipToPatient: '' as any,
    address1: '',
    address2: '',
    city: '',
    state: '' as any,
    country: '' as any,
    postal: '',
};

interface ISubscriberPayerFormProps {
    claimEditorStore: ClaimEditorStore;
    data?: CreateClaimSubscriber;
    subscriberIndex: number;
}

export const SubscriberPayerForm: React.FunctionComponent<ISubscriberPayerFormProps> = ({ claimEditorStore, data, subscriberIndex }) => {
    const sexOptions = getOptions(SexType);
    const stateOptions = getOptions(StateType);
    const countryOptions = getOptions(CountryType);
    const relationshipOptions = getOptions(RelationshipType);
    const saveTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

    const SubscriberSchema = Yup.object().shape({
        subscriberLookup: Yup.string(),
        payerLookup: Yup.string().required('Payer is required'),
        payerId: Yup.string().required('Payer is required'),
        memberId: Yup.string().required('Member ID is required'),
        first: Yup.string().required('First name is required'),
        middle: Yup.string(),
        last: Yup.string().required('Last name is required'),
        dob: Yup.string(),
        sex: Yup.mixed<SexType | ''>(),
        phone: Yup.string(),
        relationshipToPatient: Yup.mixed<RelationshipType | ''>(),
        address1: Yup.string().required('Address Line 1 is required'),
        address2: Yup.string(),
        city: Yup.string().required('City is required'),
        state: Yup.mixed<StateType | ''>(),
        country: Yup.mixed<CountryType | ''>(),
        postal: Yup.string().required('Postal Code is required'),
    });

    const convertToSubscriber = (v: SubscriberPayerFormValues): CreateClaimSubscriber => {
        const lines = [v.address1].filter(Boolean);
        if (v.address2) lines.push(v.address2);
        return {
            subscriberId: v.subscriberId || undefined,
            first: v.first || '',
            middle: v.middle || undefined as any,
            last: v.last || '',
            dob: (v.dob as any) || undefined,
            sex: (v.sex as any) || undefined,
            phone: v.phone || undefined,
            relationshipToPatient: (v.relationshipToPatient as any) || undefined,
            memberId: v.memberId || '',
            payerId: v.payerId || '',
            addresses: [
                {
                    name: `${(v.first || '').trim()} ${(v.last || '').trim()}`.trim(),
                    lines,
                    city: v.city || '',
                    state: (v.state as any) || undefined,
                    postal: v.postal || '',
                    country: (v.country as any) || undefined,
                    type: AddressType.MAILING,
                } as any,
            ],
        } as CreateClaimSubscriber;
    };

    const debouncedSave = (values: SubscriberPayerFormValues): void => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = setTimeout(() => {
            const updatedSubscriber = convertToSubscriber(values);
            const subscribers = [...(claimEditorStore.subscribers || [])];
            subscribers[subscriberIndex] = updatedSubscriber;
            claimEditorStore.updateSubscribers(subscribers);
        }, 500);
    };

    useEffect(() => {
        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    }, []);

    const initialValues = useMemo<SubscriberPayerFormValues>(() => {
        const s = data;
        if (!s) return emptyValues;
        const addr = s.addresses?.[0];
        return {
            subscriberLookup: s.last || '',
            subscriberId: (s as any).subscriberId || (s as any).id || '',
            payerLookup: s.payerId || '',
            payerId: s.payerId || '',
            memberId: s.memberId || '',
            first: s.first || '',
            middle: s.middle || '',
            last: s.last || '',
            dob: (s.dob as any) || '',
            sex: (s.sex as any) || '' as any,
            phone: s.phone || '',
            relationshipToPatient: (s as any).relationshipToPatient || '' as any,
            address1: addr?.lines?.[0] || '',
            address2: addr?.lines?.[1] || '',
            city: addr?.city || '',
            state: (addr?.state as any) || '' as any,
            country: (addr?.country as any) || '' as any,
            postal: addr?.postal || '',
        };
    }, [data]);

    return (
        <Formik<SubscriberPayerFormValues> initialValues={initialValues} validationSchema={SubscriberSchema} onSubmit={() => {}} enableReinitialize
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={false}
        >
            {formik => {
                // Auto-save on value change - save whenever values change, not just when dirty
                useEffect(() => {
                    debouncedSave(formik.values);
                }, [formik.values, debouncedSave]);

                useEffect(() => {
                    if (claimEditorStore.validationSignal > 0) {
                        formik.submitForm();
                    }
                }, [claimEditorStore.validationSignal]);

                return (
                <S.FormContainer>
                    <FormErrorBanner submitCount={formik.submitCount} isValid={formik.isValid} errors={formik.errors} />
                    <S.FormGrid>
                        <S.FormField span={2}>
                            <S.Label>Lookup Subscriber</S.Label>
                            <Field
                                name="subscriberLookup"
                                component={AsyncSelectField}
                                placeholder="Type name to search"
                                loadOptions={async (q: string) => {
                                    const results: SupportSubscriber[] = await claimEditorStore.searchSubscribersByName(q);
                                    return (results || []).map(r => ({
                                        label: [r.first, r.middle, r.last].filter(Boolean).join(' '),
                                        value: r.id as any,
                                        subscriber: r,
                                        info: ' | ' + (r.dob || '') + ' ' + (r.sex ? _.startCase(_.lowerCase(r.sex)) : ''),
                                    } as any));
                                }}
                                onChange={(opt: any, form: any) => {
                                    const sub = opt?.subscriber;
                                    if (!sub) {
                                        form.setFieldValue('subscriberId', undefined);
                                        return;
                                    }
                                    const addr = sub?.addresses?.[0];
                                    const newValues = {
                                        subscriberLookup: opt.label,
                                        subscriberId: sub?.id || '',
                                        payerLookup: form.values.payerLookup || '',
                                        payerId: form.values.payerId || '',
                                        memberId: form.values.memberId || '',
                                        first: sub?.first || '',
                                        middle: sub?.middle || '',
                                        last: sub?.last || '',
                                        dob: sub?.dob || '',
                                        sex: (sub?.sex as any) || '',
                                        phone: sub?.phone || '',
                                        relationshipToPatient: form.values.relationshipToPatient || '',
                                        address1: addr?.lines?.[0] || '',
                                        address2: addr?.lines?.[1] || '',
                                        city: addr?.city || '',
                                        state: (addr?.state as any) || '',
                                        country: (addr?.country as any) || '',
                                        postal: addr?.postal || '',
                                    };
                                    form.setValues(newValues);
                                    debouncedSave(newValues);
                                }}
                            />
                        </S.FormField>

                        <S.FormField span={2}>
                            <S.Label required>Lookup Payer</S.Label>
                            <Field
                                name="payerLookup"
                                component={AsyncSelectField}
                                placeholder="Type payer name to search"
                                loadOptions={async (q: string) => {
                                    const query = (q || '').trim().toLowerCase();

                                    // Ensure cache is loaded once
                                    if (!claimEditorStore['payersLoaded']) {
                                        await (claimEditorStore as any).preloadPayers();
                                    }

                                    const list: ProviderPayer[] = (claimEditorStore as any)['payersCache'] || [];
                                    const normalize = (val?: string) => (val ? val.toLowerCase() : '');
                                    const contains = (val?: string) => {
                                        const normalized = normalize(val);
                                        return normalized.includes(query);
                                    };

                                    const filtered = list.filter((p: ProviderPayer) => {
                                        const name = normalize(p?.name);
                                        const coverage = normalize(p?.coverage);
                                        return contains(name) || contains(coverage);
                                    });

                                    // Limit results to 10 for performance and usability
                                    return filtered.slice(0, 10).map(p => ({
                                        label: p.name || '',
                                        value: p.id as any,
                                        payer: p,
                                        info: p.coverage ? '| ' + p.coverage : undefined,
                                    } as any));
                                }}
                                getDefault={async (id: string) => {
                                    if (!id) return null;
                                    const payer = await claimEditorStore.getPayerById(id);
                                    if (!payer) return null;
                                    return {
                                        label: payer.name || '',
                                        value: payer.id as any,
                                        payer,
                                    } as any;
                                }}
                                onChange={(opt: any, form: any) => {
                                    const payer = opt?.payer;
                                    if (!payer) return;
                                    form.setFieldValue('payerLookup', String(payer.id || ''));
                                    form.setFieldValue('payerId', payer.id);
                                    // Trigger save with updated values
                                    const updatedValues = {
                                        ...form.values,
                                        payerLookup: String(payer.id || ''),
                                        payerId: payer.id,
                                    };
                                    debouncedSave(updatedValues);
                                }}
                            />
                        </S.FormField>

                        <S.FormField>
                            <S.Label required>Subscriber First Name</S.Label>
                            <BaseInput name="first" placeholder="Subscriber First Name" value={formik.values.first} onChange={formik.handleChange} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Subscriber Middle Name</S.Label>
                            <BaseInput name="middle" placeholder="Subscriber Middle Name" value={formik.values.middle} onChange={formik.handleChange} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label required>Subscriber Last Name</S.Label>
                            <BaseInput name="last" placeholder="Subscriber Last Name" value={formik.values.last} onChange={formik.handleChange} />
                        </S.FormField>

                        <S.FormField>
                            <S.Label>Subscriber Date of Birth</S.Label>
                            <Field name="dob" placeholder="YYYY-MM-DD" component={DatePickerField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Subscriber Sex</S.Label>
                            <BaseSelect
                                options={sexOptions}
                                value={sexOptions.find(opt => opt.value === formik.values.sex)}
                                onChange={(
                                    newValue: ISelectOption | MultiValue<ISelectOption> | null,
                                    actionMeta: ActionMeta<ISelectOption>
                                ) => {
                                    const option = newValue as ISelectOption | null;
                                    const value = option?.value || '';
                                    formik.setFieldValue('sex', value);
                                }}
                                placeholder="Select"
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Relationship to Patient</S.Label>
                            <BaseSelect
                                options={relationshipOptions}
                                value={relationshipOptions.find(opt => opt.value === formik.values.relationshipToPatient)}
                                onChange={(
                                    newValue: ISelectOption | MultiValue<ISelectOption> | null,
                                    actionMeta: ActionMeta<ISelectOption>
                                ) => {
                                    const option = newValue as ISelectOption | null;
                                    const value = option?.value || '';
                                    formik.setFieldValue('relationshipToPatient', value);
                                }}
                                placeholder="Select"
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label required>Member ID</S.Label>
                            <BaseInput name="memberId" placeholder="Member ID" value={formik.values.memberId} onChange={formik.handleChange} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Phone Number</S.Label>
                            <BaseInput name="phone" placeholder="Phone Number" value={formik.values.phone} onChange={formik.handleChange} />
                        </S.FormField>

                        <S.FormField span={2}>
                            <S.Label required>Address Line 1</S.Label>
                            <BaseInput name="address1" placeholder="Address Line 1" value={formik.values.address1} onChange={formik.handleChange} />
                        </S.FormField>
                        <S.FormField span={2}>
                            <S.Label>Address Line 2</S.Label>
                            <BaseInput name="address2" placeholder="Address Line 2" value={formik.values.address2} onChange={formik.handleChange} />
                        </S.FormField>

                        <S.FormField>
                            <S.Label>City</S.Label>
                            <BaseInput name="city" placeholder="City" value={formik.values.city} onChange={formik.handleChange} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>State</S.Label>
                            <BaseSelect
                                options={stateOptions}
                                value={stateOptions.find(opt => opt.value === formik.values.state)}
                                onChange={(
                                    newValue: ISelectOption | MultiValue<ISelectOption> | null,
                                    actionMeta: ActionMeta<ISelectOption>
                                ) => {
                                    const option = newValue as ISelectOption | null;
                                    const value = option?.value || '';
                                    formik.setFieldValue('state', value);
                                }}
                                placeholder="Select State"
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Country</S.Label>
                            <BaseSelect
                                options={countryOptions}
                                value={countryOptions.find(opt => opt.value === formik.values.country)}
                                onChange={(
                                    newValue: ISelectOption | MultiValue<ISelectOption> | null,
                                    actionMeta: ActionMeta<ISelectOption>
                                ) => {
                                    const option = newValue as ISelectOption | null;
                                    const value = option?.value || '';
                                    formik.setFieldValue('country', value);
                                }}
                                placeholder="Select Country"
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Postal Code</S.Label>
                            <BaseInput name="postal" placeholder="Postal Code" value={formik.values.postal} onChange={formik.handleChange} />
                        </S.FormField>
                    </S.FormGrid>
                </S.FormContainer>
                );
            }}
        </Formik>
    );
};

export default SubscriberPayerForm;


